/**
 * Roof Safety PDF Report Generator
 * Generates a branded CloudSafe compliance report for all roof safety equipment.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getAuthHeader } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';

// ─── Brand colours ──────────────────────────────────────────────
const BRAND = {
  blue:      [26, 128, 229],   // #1A80E5
  blueDark:  [21, 101, 192],   // #1565C0
  navy:      [15, 23, 42],     // #0f172a
  dark:      [17, 24, 39],     // #111827
  gray:      [100, 116, 139],  // #64748b
  grayLight: [148, 163, 184],  // #94a3b8
  border:    [226, 232, 240],  // #e2e8f0
  bgLight:   [248, 250, 252],  // #f8fafc
  green:     [34, 197, 94],    // #22c55e
  greenBg:   [240, 253, 244],  // #f0fdf4
  red:       [239, 68, 68],    // #ef4444
  redBg:     [254, 242, 242],  // #fef2f2
  amber:     [245, 158, 11],   // #f59e0b
  amberBg:   [255, 251, 235],  // #fffbeb
  white:     [255, 255, 255],
};

const EQUIPMENT_TYPES = [
  { key: 'anchorPoints',  label: 'Anchor Points',  endpoint: 'anchor-points' },
  { key: 'cradles',       label: 'Cradles',         endpoint: 'cradles' },
  { key: 'abseilSystems', label: 'Abseil Systems',  endpoint: 'abseil-systems' },
  { key: 'ladders',       label: 'Ladders',         endpoint: 'ladders' },
  { key: 'walkways',      label: 'Walkways',        endpoint: 'walkways' },
  { key: 'staticLines',   label: 'Static Lines',    endpoint: 'static-lines' },
  { key: 'handrails',     label: 'Handrails',       endpoint: 'handrails' },
];

// ─── Helpers ────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function itemName(item, fallback) {
  return item.name || item.cradle_name || item.system_name || item.location_description || `${fallback} #${item.id}`;
}

function parseNextDate(item) {
  for (const f of ['next_inspection_date', 'nextInspectionDate', 'next_test_date', 'nextTestDate']) {
    if (item?.[f]) { const d = new Date(item[f]); if (!isNaN(d.getTime())) return d; }
  }
  return null;
}

function statusLabel(s) {
  if (!s) return 'Unknown';
  const l = s.toLowerCase();
  if (l === 'pass') return 'Pass';
  if (l === 'fail') return 'Fail';
  if (l === 'pending') return 'Pending';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ─── Load logo as base64 for embedding in PDF ──────────────────
function loadLogoAsBase64() {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = cloudsafeLogo;
  });
}

// ─── Logo aspect ratio (270 x 127 original) ────────────────────
const LOGO_RATIO = 270 / 127; // ~2.13:1

// ─── PDF drawing helpers ────────────────────────────────────────
function drawHeader(doc, logoData) {
  const pw = doc.internal.pageSize.width;

  // Blue accent bar across top
  doc.setFillColor(...BRAND.blue);
  doc.rect(0, 0, pw, 4, 'F');

  // Logo in header (height-based, maintain aspect ratio)
  const hdrLogoH = 14;
  const hdrLogoW = hdrLogoH * LOGO_RATIO; // ~30
  if (logoData) {
    doc.addImage(logoData, 'PNG', 20, 10, hdrLogoW, hdrLogoH);
  }

  // Brand name (offset right to make room for logo)
  const textX = logoData ? 20 + hdrLogoW + 4 : 20;
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.blue);
  doc.text('CloudSafe', textX, 20);

  // Tagline
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.grayLight);
  doc.text('Safety Management Platform', textX, 27);

  // Date on right
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.gray);
  const now = new Date();
  doc.text(
    `Generated: ${now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}  ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
    pw - 20, 20, { align: 'right' }
  );

  // Separator
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.5);
  doc.line(20, 32, pw - 20, 32);
}

// ─── Draw watermark logo across every page ──────────────────────
function drawWatermark(doc, logoData) {
  if (!logoData) return;
  const pw = doc.internal.pageSize.width;
  const ph = doc.internal.pageSize.height;
  const gState = new doc.GState({ opacity: 0.04 });
  doc.saveGraphicsState();
  doc.setGState(gState);
  // Large centred watermark logo (maintain aspect ratio)
  const wmW = 120;
  const wmH = wmW / LOGO_RATIO; // ~56
  doc.addImage(logoData, 'PNG', (pw - wmW) / 2, (ph - wmH) / 2, wmW, wmH);
  doc.restoreGraphicsState();
}

function drawFooter(doc, pageNum, totalPages) {
  const ph = doc.internal.pageSize.height;
  const pw = doc.internal.pageSize.width;

  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.3);
  doc.line(20, ph - 18, pw - 20, ph - 18);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.grayLight);
  doc.text('CloudSafe  |  Roof Safety Compliance Report  |  Confidential', 20, ph - 11);
  doc.text(`Page ${pageNum} of ${totalPages}`, pw - 20, ph - 11, { align: 'right' });
}

function sectionTitle(doc, y, title) {
  const pw = doc.internal.pageSize.width;
  doc.setFillColor(...BRAND.blue);
  doc.roundedRect(20, y - 5, pw - 40, 10, 2, 2, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.white);
  doc.text(title, 25, y + 2);
  return y + 14;
}

function subHeading(doc, y, text) {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.navy);
  doc.text(text, 20, y);
  return y + 8;
}

function checkPageBreak(doc, y, needed, logoData) {
  const ph = doc.internal.pageSize.height;
  if (y + needed > ph - 30) {
    doc.addPage();
    drawHeader(doc, logoData);
    return 44;
  }
  return y;
}

// ─── Status colour for table cells ──────────────────────────────
function statusCellColor(status) {
  const s = (status || '').toLowerCase();
  if (s === 'pass') return BRAND.green;
  if (s === 'fail') return BRAND.red;
  if (s === 'pending') return BRAND.amber;
  return BRAND.gray;
}

// ═══════════════════════════════════════════════════════════════════
//  Main export function
// ═══════════════════════════════════════════════════════════════════
export async function generateRoofSafetyReport(propertyId) {
  const headers = getAuthHeader();
  const now = new Date();

  // 0. Load logo for embedding
  const logoData = await loadLogoAsBase64();

  // 1. Fetch property info
  let propertyName = `Property ${propertyId}`;
  let propertyAddress = '';
  try {
    const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}`), { headers });
    if (res.ok) {
      const p = await res.json();
      propertyName = p.name || p.property_name || propertyName;
      propertyAddress = p.address || p.location || '';
    }
  } catch (_) {}

  // 2. Fetch all equipment data in parallel
  const responses = await Promise.all(
    EQUIPMENT_TYPES.map(e =>
      fetch(getApiEndpoint(`/api/properties/${propertyId}/${e.endpoint}`), { headers })
        .then(r => r.ok ? r.json() : [])
        .catch(() => [])
    )
  );

  const allData = EQUIPMENT_TYPES.map((eq, idx) => {
    const arr = Array.isArray(responses[idx]) ? responses[idx] : [];
    return { ...eq, items: arr };
  });

  // 3. Compute totals
  let totalAssets = 0, totalPassed = 0, totalFailed = 0, totalDueSoon = 0, totalOverdue = 0, totalPending = 0;
  const categorySummary = allData.map(cat => {
    const items = cat.items;
    const passed = items.filter(i => (i.status || '').toLowerCase() === 'pass').length;
    const failed = items.filter(i => (i.status || '').toLowerCase() === 'fail').length;
    const pending = items.filter(i => ['pending', 'unknown', ''].includes((i.status || '').toLowerCase())).length;
    let dueSoon = 0, overdue = 0;
    items.forEach(i => {
      const nd = parseNextDate(i);
      if (!nd) return;
      const diff = Math.ceil((nd - now) / (1000 * 60 * 60 * 24));
      if (diff < 0 && (i.status || '').toLowerCase() !== 'fail') overdue++;
      else if (diff >= 0 && diff <= 30 && (i.status || '').toLowerCase() !== 'fail') dueSoon++;
    });
    totalAssets += items.length;
    totalPassed += passed;
    totalFailed += failed;
    totalDueSoon += dueSoon;
    totalOverdue += overdue;
    totalPending += pending;
    return { ...cat, passed, failed, pending, dueSoon, overdue };
  });

  const complianceRate = totalAssets > 0 ? Math.round((totalPassed / totalAssets) * 100) : 0;

  // ─── Create PDF ─────────────────────────────────────────────
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.width;

  // ── PAGE 1: COVER ───────────────────────────────────────────
  // Full blue header block
  doc.setFillColor(...BRAND.blue);
  doc.rect(0, 0, pw, 105, 'F');

  // Logo + brand name on same baseline, vertically centered
  const cvLogoH = 18;
  const cvLogoW = cvLogoH * LOGO_RATIO; // ~38
  const brandY = 22; // vertical centre line for logo + text row
  if (logoData) {
    doc.addImage(logoData, 'PNG', 20, brandY - cvLogoH / 2, cvLogoW, cvLogoH);
  }
  const brandTextX = logoData ? 20 + cvLogoW + 5 : 20;
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.white);
  doc.text('CloudSafe', brandTextX, brandY + 3);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 215, 235);
  doc.text('Safety Management Platform', brandTextX, brandY + 12);

  // Report title — left-aligned, below brand row
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.white);
  doc.text('Roof Safety Compliance Report', 20, 65);

  // Thin white separator inside blue block
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  doc.line(20, 72, pw - 20, 72);

  // Report subtitle
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 215, 235);
  doc.text('Full equipment inspection and compliance summary', 20, 80);

  // Property info box
  let cy = 115;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.gray);
  doc.text('PROPERTY', 20, cy);
  cy += 8;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.navy);
  doc.text(propertyName, 20, cy);
  if (propertyAddress) {
    cy += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.gray);
    doc.text(propertyAddress, 20, cy);
  }

  // Report date
  cy += 20;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.gray);
  doc.text('REPORT DATE', 20, cy);
  cy += 8;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.navy);
  doc.text(now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }), 20, cy);

  // Quick stats on cover
  cy += 25;
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.5);
  doc.line(20, cy, pw - 20, cy);
  cy += 15;

  const coverStats = [
    { label: 'Total Assets', value: totalAssets.toString(), color: BRAND.blue },
    { label: 'Compliant', value: totalPassed.toString(), color: BRAND.green },
    { label: 'Failed', value: totalFailed.toString(), color: BRAND.red },
    { label: 'Due Soon', value: totalDueSoon.toString(), color: BRAND.amber },
    { label: 'Compliance', value: `${complianceRate}%`, color: BRAND.blue },
  ];

  const statWidth = (pw - 40) / coverStats.length;
  coverStats.forEach((s, i) => {
    const x = 20 + i * statWidth;
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...s.color);
    doc.text(s.value, x + statWidth / 2, cy, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.gray);
    doc.text(s.label, x + statWidth / 2, cy + 8, { align: 'center' });
  });

  // Confidential footer on cover
  const ph = doc.internal.pageSize.height;
  doc.setFontSize(7);
  doc.setTextColor(...BRAND.grayLight);
  doc.text('CONFIDENTIAL — This report is generated by CloudSafe and intended for authorised personnel only.', pw / 2, ph - 15, { align: 'center' });

  // ── PAGE 2: EXECUTIVE SUMMARY ──────────────────────────────
  doc.addPage();
  drawHeader(doc, logoData);
  cy = 44;

  cy = sectionTitle(doc, cy, 'Executive Summary');
  cy += 2;

  // Summary table
  autoTable(doc, {
    startY: cy,
    head: [['Equipment Type', 'Total', 'Pass', 'Fail', 'Pending', 'Due Soon', 'Overdue']],
    body: categorySummary.map(c => [
      c.label,
      c.items.length.toString(),
      c.passed.toString(),
      c.failed.toString(),
      c.pending.toString(),
      c.dueSoon.toString(),
      c.overdue.toString(),
    ]),
    foot: [['TOTAL', totalAssets.toString(), totalPassed.toString(), totalFailed.toString(), totalPending.toString(), totalDueSoon.toString(), totalOverdue.toString()]],
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 4, lineColor: BRAND.border, lineWidth: 0.3 },
    headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: BRAND.navy, textColor: BRAND.white, fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { halign: 'left', cellWidth: 38 },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'center' },
      6: { halign: 'center' },
    },
    alternateRowStyles: { fillColor: BRAND.bgLight },
    margin: { left: 20, right: 20 },
    didParseCell: function(data) {
      if (data.section === 'body') {
        // Colour failed cells red
        if (data.column.index === 3 && parseInt(data.cell.text[0]) > 0) {
          data.cell.styles.textColor = BRAND.red;
          data.cell.styles.fontStyle = 'bold';
        }
        // Colour overdue cells amber
        if (data.column.index === 6 && parseInt(data.cell.text[0]) > 0) {
          data.cell.styles.textColor = BRAND.red;
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 5 && parseInt(data.cell.text[0]) > 0) {
          data.cell.styles.textColor = BRAND.amber;
          data.cell.styles.fontStyle = 'bold';
        }
      }
    }
  });

  cy = doc.lastAutoTable.finalY + 16;

  // Compliance rate callout box
  cy = checkPageBreak(doc, cy, 35, logoData);
  const boxW = pw - 40;
  doc.setFillColor(...(complianceRate >= 80 ? BRAND.greenBg : complianceRate >= 50 ? BRAND.amberBg : BRAND.redBg));
  doc.roundedRect(20, cy, boxW, 28, 3, 3, 'F');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...(complianceRate >= 80 ? BRAND.green : complianceRate >= 50 ? BRAND.amber : BRAND.red));
  doc.text(`Overall Compliance Rate: ${complianceRate}%`, 30, cy + 12);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.gray);
  const riskText = totalFailed > 0 ? 'High Risk — Immediate action required on failed equipment.' : totalOverdue > 0 ? 'Medium Risk — Overdue inspections need scheduling.' : totalDueSoon > 0 ? 'Low Risk — Upcoming inspections due within 30 days.' : 'All Clear — All equipment is compliant.';
  doc.text(riskText, 30, cy + 22);
  cy += 40;

  // ── ALERTS SECTION ─────────────────────────────────────────
  const alerts = [];
  categorySummary.forEach(cat => {
    cat.items.forEach(item => {
      const status = (item.status || '').toLowerCase();
      const nd = parseNextDate(item);
      if (status === 'fail') {
        alerts.push({ severity: 'CRITICAL', type: cat.label, name: itemName(item, cat.label), issue: 'Failed inspection', date: fmtDate(item.last_inspection_date || item.updated_at) });
      }
      if (nd && nd < now && status !== 'fail') {
        const days = Math.ceil((now - nd) / (1000 * 60 * 60 * 24));
        alerts.push({ severity: 'CRITICAL', type: cat.label, name: itemName(item, cat.label), issue: `Overdue by ${days} days`, date: fmtDate(nd) });
      }
      if (nd && status !== 'fail') {
        const diff = Math.ceil((nd - now) / (1000 * 60 * 60 * 24));
        if (diff >= 0 && diff <= 30) {
          alerts.push({ severity: 'WARNING', type: cat.label, name: itemName(item, cat.label), issue: diff === 0 ? 'Due today' : `Due in ${diff} days`, date: fmtDate(nd) });
        }
      }
    });
  });

  if (alerts.length > 0) {
    cy = checkPageBreak(doc, cy, 30, logoData);
    cy = sectionTitle(doc, cy, `Active Alerts (${alerts.length})`);
    cy += 2;

    autoTable(doc, {
      startY: cy,
      head: [['Severity', 'Equipment Type', 'Asset', 'Issue', 'Date']],
      body: alerts.map(a => [a.severity, a.type, a.name, a.issue, a.date]),
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3.5, lineColor: BRAND.border, lineWidth: 0.3 },
      headStyles: { fillColor: BRAND.navy, textColor: BRAND.white, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: BRAND.bgLight },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 45 },
        4: { cellWidth: 28 },
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 0) {
          if (data.cell.text[0] === 'CRITICAL') {
            data.cell.styles.textColor = BRAND.red;
          } else {
            data.cell.styles.textColor = BRAND.amber;
          }
        }
      }
    });
    cy = doc.lastAutoTable.finalY + 16;
  }

  // ── EQUIPMENT DETAIL PAGES ─────────────────────────────────
  categorySummary.forEach(cat => {
    if (cat.items.length === 0) return;

    doc.addPage();
    drawHeader(doc, logoData);
    cy = 44;

    cy = sectionTitle(doc, cy, cat.label);
    cy += 2;

    // Quick stats row for this category
    const catStats = [
      { l: 'Total', v: cat.items.length, c: BRAND.blue },
      { l: 'Pass', v: cat.passed, c: BRAND.green },
      { l: 'Fail', v: cat.failed, c: BRAND.red },
      { l: 'Pending', v: cat.pending, c: BRAND.gray },
      { l: 'Due Soon', v: cat.dueSoon, c: BRAND.amber },
    ];

    const csw = (pw - 40) / catStats.length;
    catStats.forEach((s, i) => {
      const x = 20 + i * csw;
      doc.setFillColor(...BRAND.bgLight);
      doc.roundedRect(x + 1, cy, csw - 2, 20, 2, 2, 'F');
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...s.c);
      doc.text(s.v.toString(), x + csw / 2, cy + 10, { align: 'center' });
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BRAND.gray);
      doc.text(s.l, x + csw / 2, cy + 17, { align: 'center' });
    });
    cy += 28;

    // Detail table
    const tableBody = cat.items.map(item => {
      const nd = parseNextDate(item);
      let dueStat = '—';
      if (nd) {
        const diff = Math.ceil((nd - now) / (1000 * 60 * 60 * 24));
        if (diff < 0) dueStat = `${Math.abs(diff)}d overdue`;
        else if (diff === 0) dueStat = 'Today';
        else if (diff <= 30) dueStat = `${diff}d`;
        else dueStat = `${diff}d`;
      }

      return [
        itemName(item, cat.label),
        (item.location_description || item.location || '—').substring(0, 30),
        item.type || '—',
        statusLabel(item.status),
        fmtDate(item.last_inspection_date || item.lastTestDate),
        fmtDate(nd),
        dueStat,
      ];
    });

    autoTable(doc, {
      startY: cy,
      head: [['Name / ID', 'Location', 'Type', 'Status', 'Last Inspection', 'Next Due', 'Days']],
      body: tableBody,
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 3, lineColor: BRAND.border, lineWidth: 0.3 },
      headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: BRAND.bgLight },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 30 },
        2: { cellWidth: 22 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 18, halign: 'center' },
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 3) {
          const val = data.cell.text[0];
          if (val === 'Fail') { data.cell.styles.textColor = BRAND.red; data.cell.styles.fontStyle = 'bold'; }
          else if (val === 'Pass') { data.cell.styles.textColor = BRAND.green; data.cell.styles.fontStyle = 'bold'; }
          else if (val === 'Pending') { data.cell.styles.textColor = BRAND.amber; }
        }
        if (data.section === 'body' && data.column.index === 6) {
          const val = data.cell.text[0];
          if (val.includes('overdue')) { data.cell.styles.textColor = BRAND.red; data.cell.styles.fontStyle = 'bold'; }
          else if (val === 'Today') { data.cell.styles.textColor = BRAND.amber; data.cell.styles.fontStyle = 'bold'; }
        }
      }
    });
  });

  // ── WATERMARKS + FOOTERS ON ALL PAGES ─────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawWatermark(doc, logoData);
    if (i >= 2) {
      drawFooter(doc, i, totalPages);
    }
  }

  // ── DOWNLOAD ───────────────────────────────────────────────
  const safeName = propertyName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
  const timestamp = now.toISOString().split('T')[0];
  const filename = `CloudSafe_Roof_Safety_Report_${safeName}_${timestamp}.pdf`;
  doc.save(filename);

  return { success: true, filename, totalAssets, complianceRate };
}
